import { CAMERA, COLOR_MATCH, PLAYER, SHAKE } from '../constants';
import {
  sendSnakeState, sendInput, sendRespawn, getLiveClient,
  onSnakeDied, onSnakeAte,
} from '../../net/liveSession';
import { getLocalServerPlayer, serverPlayers } from '../../net/serverWorld';
import type { ClientSnakeState, ClientInput } from '../../net/messages';
import { RemoteSnakeRenderer } from '../render/RemoteSnakeRenderer';
import { LiveWorldRenderer } from '../render/LiveWorldRenderer';
import { LocalPrediction } from '../sim/LocalPrediction';
import { useGameStore } from '../../store/gameStore';
import { audio } from '../audio/AudioEngine';
import { CelebrationSystem } from '../effects/Celebration';
import { DustField } from '../effects/Dust';
import { ToastSystem } from '../effects/Toasts';
import type { Snake } from '../entities/Snake';
import { CelebrationRenderer } from '../render/CelebrationRenderer';
import { DustRenderer } from '../render/DustRenderer';
import { FoodRenderer } from '../render/FoodRenderer';
import { Minimap } from '../render/Minimap';
import { ParticleRenderer } from '../render/ParticleRenderer';
import { PowerupRenderer } from '../render/PowerupRenderer';
import { SnakeRenderer } from '../render/SnakeRenderer';
import { SpeedLines } from '../render/SpeedLines';
import { ToastRenderer } from '../render/ToastRenderer';
import { InputManager } from '../input/InputManager';
import { clamp, damp } from '../utils/math';
import { Grid } from '../world/Grid';
import { World } from '../world/World';
import { Camera } from './Camera';
import { GameLoop } from './GameLoop';

interface HudPush {
  fps: number;
  mass: number;
  radius: number;
  posX: number;
  posY: number;
  zoom: number;
  alive: boolean;
  playerHue: number;
  combo: number;
  bestMass: number;
  muted: boolean;
  paused: boolean;
  audioLocked: boolean;
  surgeRemaining: number;
  doubleRemaining: number;
  shielded: boolean;
  kills: number;
  /** Seconds remaining in the current combo window (0 = no active combo). */
  comboRemaining: number;
  /** Set when the player dies — surfaced by DeathModal as "killed by X".
   *  Cleared on respawn. Null whenever the player is alive or died via
   *  some other cause (we currently only support snake-on-snake kills). */
  killedByName: string | null;
  killedByHue: number | null;
  /** Death recap state — when active, the HUD shows the recap overlay
   *  + suppresses the DeathModal. The engine drives this on/off; the
   *  HUD only reads. */
  recapActive: boolean;
  recapKillerName: string | null;
  recapKillerHue: number | null;
  recapRank: number;
  recapFinalMass: number;
  /** Seconds the player has been alive in the current life. Resets to
   *  0 on each respawn. Surfaced for HUD components (anticipation
   *  countdown for the 60s reward, milestone toasts) — components
   *  derive their own thresholds; the engine just publishes the raw
   *  number. */
  aliveSeconds: number;
}

const BEST_MASS_KEY = 'cellsnake:bestMass';

function loadBestMass(): number {
  try {
    const v = Number(localStorage.getItem(BEST_MASS_KEY) ?? '0');
    return Number.isFinite(v) ? v : 0;
  } catch { return 0; }
}
function saveBestMass(v: number): void {
  try { localStorage.setItem(BEST_MASS_KEY, String(Math.round(v))); } catch { /* noop */ }
}

interface LeaderRow {
  id: number | string;
  name: string;
  mass: number;
  isPlayer: boolean;
  hue: number;
  playerRank?: number | null;
}

/**
 * GameEngine — top-level coordinator.
 *
 * Owns: canvas context, camera, renderers, input, world simulation, loop.
 * Wires world-emitted effects (color-match bursts, death events) to the
 * particle system and camera shake. Pushes HUD/leaderboard snapshots to
 * Zustand at fixed cadences (10Hz / 4Hz).
 */
export class GameEngine {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly camera = new Camera();
  private readonly grid = new Grid();
  private readonly world: World;
  private readonly snakeRenderer = new SnakeRenderer();
  /** Phase-2-Lite — paints remote players' snakes (received via the
   *  relay) over the same scene as the local simulation. Only used
   *  when neither live mode nor server-state rendering is active. */
  private readonly remoteSnakeRenderer = new RemoteSnakeRenderer();
  /** Phase-2-Full Session 4 — renders the server's authoritative
   *  state.players + state.food when the engine is in live mode. */
  private readonly liveWorldRenderer = new LiveWorldRenderer();
  private readonly foodRenderer = new FoodRenderer();
  private readonly powerupRenderer = new PowerupRenderer();
  private readonly particleRenderer = new ParticleRenderer();
  private readonly minimap = new Minimap();
  /** When false, the engine skips drawing the bottom-right minimap.
   *  Mobile sets this via GameCanvas so the small screen isn't
   *  cluttered with a radar the player doesn't need (they can see
   *  threats by simply looking at the visible arena). */
  private renderMinimap = true;
  public setMinimapEnabled(on: boolean): void { this.renderMinimap = on; }
  private readonly toasts = new ToastSystem();
  private readonly toastRenderer = new ToastRenderer();
  private readonly dust = new DustField();
  private readonly dustRenderer = new DustRenderer();
  private readonly speedLines = new SpeedLines();
  private readonly celebration = new CelebrationSystem();
  private readonly celebrationRenderer = new CelebrationRenderer();
  /** Singleton — see AudioEngine.ts. Shared so Landing can pre-unlock it. */
  private readonly audio = audio;
  private readonly input: InputManager;
  private readonly loop: GameLoop;

  /** Slow-motion factor — damps to 0.3 on player death, 1.0 alive. */
  private timeScale = 1;
  /**
   * Music intensity 0..1, mirrors the player's progression. Damps slowly so
   * each pellet doesn't make the music lurch — only sustained growth swells
   * the orchestration.
   */
  private musicIntensity = 0;
  /** Combo streak — counts consecutive perfect eats within 1.5s. */
  private combo = 0;
  private comboTimeout = 0;
  /** Tracks the last boost state so we know when to start/stop boost audio. */
  private wasBoosting = false;
  /** Per-engine guard so the 60s reward callback fires at most once
   *  even if the player stays alive for hours. Cross-session
   *  deduplication is the store's job — see fireSessionReward. */
  private sessionRewardFired = false;
  /** Engine-level callback for the 60s "session reward" beat. GameCanvas
   *  wires it to gameStore.fireSessionReward on mount. */
  public onSessionReward: (() => void) | null = null;
  /** Set by GameCanvas at construction if the store already says the
   *  reward fired earlier this browser session — prevents the timer
   *  from firing again when the user navigates home and back. */
  public markSessionRewardConsumed(): void {
    this.sessionRewardFired = true;
  }
  /** Best mass ever (localStorage-backed). Updated on death if surpassed. */
  private bestMass = 0;
  /** Kill count for the current life (resets on respawn). */
  private kills = 0;
  /** Highest 1000-mass milestone fired this run (resets on respawn). */
  private milestoneFired = 0;
  /** Paused when tab loses focus — physics halts, render keeps drawing. */
  private paused = false;
  private deathBestUpdated = false;

  private dpr = 1;

  /** 10 Hz Phase-2-Lite snake_state sender. setInterval handle so we
   *  can stop it cleanly on dispose. No-op when the live session isn't
   *  connected (sendSnakeState short-circuits) so this can run
   *  unconditionally without coupling to liveSession status changes. */
  private snakeStateTimer: number | null = null;
  /** 30 Hz Phase-2-Full input sender. Activated only when the engine
   *  is in live mode. Sends ClientInput payloads to drive the
   *  server's authoritative snake physics. */
  private inputTimer: number | null = null;
  /** Whether the engine is currently in server-authoritative live mode.
   *  Set once at start() based on store.gameMode — gameMode doesn't
   *  change during a game session (the only transitions are home
   *  → game and game → home, both of which dispose/recreate engine). */
  private isLiveMode = false;
  /** Unsubscribe from the snake_died message stream. */
  private offSnakeDied: (() => void) | null = null;
  /** Unsubscribe from the snake_ate message stream. */
  private offSnakeAte: (() => void) | null = null;
  /** Phase-2-Full Session 5 — client-side prediction for the local
   *  player. In live mode, the engine drives this each tick with the
   *  current mouseWorld + boost, and reconciles toward serverWorld on
   *  every patch. The Snake inside is what the renderer draws for the
   *  local player; the camera follows its head. */
  private prediction: LocalPrediction | null = null;
  private ro: ResizeObserver | null = null;
  /** Set true after dispose() so any in-flight deferred work bails. */
  private disposed = false;
  /** rAF handle for the deferred first-frame schedule (start()). */
  private deferredStartRaf = 0;

  private hudAccum = 0;
  private readonly hudInterval = 0.1;
  private readonly onHud: (h: Partial<HudPush>) => void;

  private leaderAccum = 0;
  private readonly leaderInterval = 0.25;
  private readonly onLeaderboard: (rows: LeaderRow[]) => void;

  private time = 0;

  /** Eject cooldown — set when player ejects, decremented each frame. */
  private ejectCooldown = 0;

  /** Render in light-theme palette (light bg + dark grid). Toggled by
   *  the host React layer (GameCanvas) when the user flips the theme
   *  preference. The engine doesn't know about React's store — the
   *  setter is the seam. */
  private lightMode = false;

  /**
   * Hit-stop microfreeze. When the player scores a kill we freeze world
   * simulation for ~60ms — the rendered frame still composites (so HUD
   * juice keeps animating) but the world stands still, giving the kill
   * a satisfying "thunk". Decremented each frame; gates world.update().
   */
  private hitStop = 0;

  /** Cooldown on the approach-warning audio so it can't spam when a
   *  big rival hovers at the edge of the warning zone. */
  private approachWarnCooldown = 0;
  /** IDs of snakes already inside the warning zone last tick — so we
   *  only play the "uh-oh" sound on FIRST entry, not every frame. */
  private prevThreats = new Set<number>();

  /**
   * Death-recap state machine. Active for ~1.5s after the player dies —
   * during this window the camera locks onto the killer's head with a
   * tighter zoom, and the HUD shows a "Killed by X · Rank #N · Mass M"
   * overlay instead of the immediate Play Again modal.
   *
   * Player can skip via the overlay button (engine.skipRecap()) which
   * zeros `remaining` and surrenders the camera back to the normal flow.
   */
  private recap = {
    active: false,
    killer: null as Snake | null,
    /** Cached identity so the HUD survives even if the killer dies mid-recap. */
    killerName: '',
    killerHue: 0,
    /** Player's leaderboard rank at the instant of death (1-based). */
    rank: 0,
    /** Player's final mass at death (rounded). */
    finalMass: 0,
    /** Seconds remaining in the recap window. */
    remaining: 0,
    /** Total window duration — also used by the overlay for its progress bar. */
    duration: 1.5,
  };

  /** Death-side-effects throttle. */
  private deathTriggeredForPlayer = false;

  constructor(
    canvas: HTMLCanvasElement,
    onHud: (h: Partial<HudPush>) => void,
    onLeaderboard: (rows: LeaderRow[]) => void,
    opts: { playerHue?: number; playerPattern?: import('../entities/Snake').SkinPattern } = {},
  ) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true });
    if (!ctx) throw new Error('2D context unavailable');
    this.ctx = ctx;
    this.onHud = onHud;
    this.onLeaderboard = onLeaderboard;

    this.input = new InputManager(canvas);
    // Unlock the Web Audio context on the very first user input — browser
    // autoplay policy requires a gesture before audio can play. Right after
    // unlocking we fire a tiny spawn swoop so the player hears the game come
    // alive on their first click/keypress.
    this.input.onFirstInteraction = () => {
      this.audio.unlock();
      setTimeout(() => this.audio.spawn(this.world.player.teamHue), 80);
    };
    this.input.onMuteToggle = () => this.audio.toggleMute();
    this.input.onPauseChange = (paused) => {
      this.paused = paused;
      if (paused) this.audio.boostStop();
      this.onHud({ paused });
    };
    this.input.onPauseToggle = () => this.togglePause();
    this.bestMass = loadBestMass();
    this.loop = new GameLoop(this.update, this.render);

    // World needs effects callbacks bound first.
    this.world = new World({
      onMatchEat: (x, y, hue) => {
        if (this.comboTimeout > 0) this.combo += 1;
        else this.combo = 1;
        this.comboTimeout = 1.5;
        const comboMult = Math.min(3, 1 + (this.combo - 1) * 0.5);
        this.world.particles.burst(
          x, y, hue,
          Math.round(COLOR_MATCH.BURST_COUNT * comboMult),
          COLOR_MATCH.BURST_SPEED * (0.9 + 0.15 * (this.combo - 1)),
        );
        this.audio.eatPerfect(hue);
        // Premium celebration burst on combo ≥4 — bigger fanfare for bigger streaks.
        if (this.combo >= 4) {
          const intensity = Math.min(2.0, 0.85 + 0.25 * (this.combo - 3));
          this.celebration.trigger(x, y, hue, intensity);
        }
      },
      onEat: (snake, x, y, hue, mass, mult) => {
        if (snake.isPlayer) {
          this.toasts.spawn({ x, y, hue, mass, multiplier: mult });
          // Regular (non-perfect) eat sound. Perfect eats already trigger the
          // eatPerfect arpeggio via onMatchEat.
          if (mult < COLOR_MATCH.PERFECT_MULT) this.audio.eat(hue);
        }
      },
      onTongueFlick: (snake) => {
        if (snake.isPlayer) this.audio.tongueHiss();
      },
      onPowerupPickup: (kind, x, y) => {
        this.audio.powerup(kind);
        // Pickup celebration — small color burst in the powerup's hue.
        const hue = kind === 'surge' ? 195 : kind === 'shield' ? 320 : 48;
        this.world.particles.burst(x, y, hue, 12, 200);
        // Mini celebration ring (lighter than kill/combo bursts).
        this.celebration.trigger(x, y, hue, 0.7);
      },
      onShieldBreak: (x, y) => {
        this.audio.shieldBreak();
        // Magenta burst at the impact site.
        this.world.particles.burst(x, y, 320, 16, 240);
        this.camera.addShake(4);
      },
      onDeathFlash: (x, y, hue, victimMass) => {
        // Screen-space color flash on every death. Intensity scales with
        // victim mass (bigger = more dopamine) and proximity (offscreen
        // deaths shouldn't blast the screen). Player-death always punches
        // because we ARE the death.
        const player = this.world.player;
        const isPlayerDeath = !player.alive && Math.hypot(x - player.head.x, y - player.head.y) < 1;
        if (isPlayerDeath) {
          this.camera.addFlash(0.55, hue);
          return;
        }
        const dx = x - player.head.x;
        const dy = y - player.head.y;
        const d = Math.hypot(dx, dy);
        const near = 1 - Math.min(1, d / SHAKE.BOT_NEAR_RANGE);
        if (near <= 0) return;
        // Scale 0.10 .. 0.45 by mass band 200 .. 6000.
        const massT = Math.max(0, Math.min(1, (victimMass - 200) / 5800));
        const amp = (0.12 + 0.32 * massT) * near;
        this.camera.addFlash(amp, hue);
      },
      onSnakeDeath: (snake, killedByPlayer, killer) => {
        if (snake.isPlayer) {
          this.camera.addShake(SHAKE.DEATH_PLAYER);
          this.audio.death();
          this.audio.boostStop();
          // Surface the killer's identity to the HUD so DeathModal can
          // display "killed by KAIRO" instead of the generic "you died".
          this.onHud({
            killedByName: killer.name,
            killedByHue: killer.teamHue,
          });
          // Kick off the death-recap cinematic. The camera-override +
          // overlay run for 1.5s, after which the normal DeathModal
          // takes over. Player can Skip via skipRecap().
          this.startRecap(snake, killer);
        } else {
          // ONLY shake when YOU killed someone nearby — random bot-vs-bot
          // deaths shouldn't rattle the camera (noise, not signal). The
          // kill sound only plays for player-credited kills too.
          if (killedByPlayer) {
            this.kills++;
            // Hit-stop: 60ms freeze so the kill registers as a "thunk".
            // Capped at 0.08s to never feel laggy.
            this.hitStop = Math.min(0.08, this.hitStop + 0.06);
            const dx = snake.head.x - this.world.player.head.x;
            const dy = snake.head.y - this.world.player.head.y;
            const d = Math.hypot(dx, dy);
            if (d < SHAKE.BOT_NEAR_RANGE) {
              const t = 1 - d / SHAKE.BOT_NEAR_RANGE;
              this.camera.addShake(SHAKE.DEATH_BOT_NEAR * t);
            }
            this.audio.kill(snake.teamHue);
            // Premium celebration on player kills.
            const intensity = Math.min(2.0, 0.9 + snake.mass / 4000);
            this.celebration.trigger(snake.head.x, snake.head.y, snake.teamHue, intensity);
            // Floating "KILL" toast at the impact point — distinct from
            // eat toasts. Named rivals (profileIdx !== undefined) get a
            // text override with their name; filler bots show the mass
            // gain since their generic names don't carry identity.
            const isNamedRival = snake.profileIdx !== undefined;
            this.toasts.spawn({
              x: snake.head.x,
              y: snake.head.y,
              hue: snake.teamHue,
              mass: Math.round(snake.mass * 0.5),
              multiplier: 3, // forces "perfect" styling — big, bold
              text: isNamedRival ? `KILL · ${snake.name}` : undefined,
              lifeOverride: isNamedRival ? 1.6 : undefined,
            });
          }
        }
      },
    }, {
      ...(opts.playerHue !== undefined ? { playerHue: opts.playerHue } : {}),
      ...(opts.playerPattern !== undefined ? { playerPattern: opts.playerPattern } : {}),
    });

    const head = this.world.player.head;
    this.camera.snapTo(head.x, head.y);
    this.camera.setZoomTarget(CAMERA.START_ZOOM);
  }

  start(): void {
    this.attachResize();
    this.resize();
    // Defensive deferred-start: when GameCanvas mounts (esp. after switching
    // from Landing), the canvas may not yet have final layout dimensions —
    // the initial getBoundingClientRect() can return 0×0 producing a black
    // first paint. Waiting one rAF lets layout settle, then we re-measure
    // and start the loop.
    //
    // The deferred handle is tracked so dispose() can cancel it. Otherwise
    // React StrictMode's mount→unmount→mount cycle in dev would start
    // *two* engine loops on the same canvas (the first engine's deferred
    // rAF would fire after dispose).
    this.deferredStartRaf = requestAnimationFrame(() => {
      this.deferredStartRaf = 0;
      if (this.disposed) return;
      this.resize();
      this.loop.start();
    });

    // Pin the mode at start. gameMode === 'live' means the player
    // joined an authoritative arena; 'training' / 'spectating' keep
    // the legacy local-simulation path. Mode doesn't change mid-
    // session — exiting to home disposes the engine.
    this.isLiveMode = useGameStore.getState().gameMode === 'live';

    if (this.isLiveMode) {
      // Phase-2-Full: input loop at 30 Hz. We keep the cadence at the
      // server's tick rate so input doesn't pile up between ticks
      // (over-sending wastes bandwidth and the server only reads
      // the most recent value anyway).
      this.inputTimer = window.setInterval(this.sendInputTick, 33);
      // Wire snake_died for particle bursts + audio + leader badge
      // refresh. liveSession fans the message to every listener.
      this.offSnakeDied = onSnakeDied((e) => this.handleSnakeDied(e));
      // snake_ate fires our local visual reactions when WE eat:
      //   - chomp animation on the mouth
      //   - pulse + digesting orb on perfect color matches
      //   - +N toast at the pellet location
      // Bots' eats are filtered out — we don't render that visual.
      this.offSnakeAte = onSnakeAte((e) => this.handleSnakeAte(e));
      // Spawn the predicted snake. Default identity from the store —
      // the first server patch will reconcile its position + mass to
      // the real spawn within ~50 ms.
      const store = useGameStore.getState();
      const sessionId = getLiveClient()?.sessionId ?? '';
      this.prediction = new LocalPrediction(
        store.playerName || 'YOU',
        store.chosenHue,
        sessionId,
      );
    } else {
      // Legacy / training: Phase-2-Lite snake_state at 10 Hz. Same
      // path the game shipped with before the switchover. No effect
      // when there's no live session.
      this.snakeStateTimer = window.setInterval(this.sendSnakeStateTick, 100);
    }
  }

  dispose(): void {
    this.disposed = true;
    if (this.deferredStartRaf) {
      cancelAnimationFrame(this.deferredStartRaf);
      this.deferredStartRaf = 0;
    }
    if (this.snakeStateTimer !== null) {
      clearInterval(this.snakeStateTimer);
      this.snakeStateTimer = null;
    }
    if (this.inputTimer !== null) {
      clearInterval(this.inputTimer);
      this.inputTimer = null;
    }
    if (this.offSnakeDied) {
      this.offSnakeDied();
      this.offSnakeDied = null;
    }
    if (this.offSnakeAte) {
      this.offSnakeAte();
      this.offSnakeAte = null;
    }
    this.prediction = null;
    this.loop.stop();
    this.input.dispose();
    this.audio.boostStop();
    this.ro?.disconnect();
    this.ro = null;
  }

  /**
   * Phase-2-Full input tick (live mode only). Reads the current
   * mouseWorld + boost state, sends to server. The server's physics
   * tick reads the freshest of these into PlayerState.steerX/Y/
   * boosting and applies the same steering math the client used
   * to run locally.
   */
  private sendInputTick = (): void => {
    if (this.paused) return;
    // mouseWorld is updated each render frame via InputManager —
    // here we just read the latest. boost button maps to .boost.
    const payload: ClientInput = {
      sx: this.input.mouseWorld.x,
      sy: this.input.mouseWorld.y,
      b:  this.input.boost ? 1 : 0,
    };
    sendInput(payload);
  };

  /**
   * Handle a `snake_died` broadcast from the server. Plays the same
   * visual + audio reaction the legacy local kill path produced,
   * just driven by an event from elsewhere.
   *
   *   - Particle burst at the death point in the snake's hue
   *   - Kill audio if the local player got the credit
   *   - Death audio if the local player IS the dead one
   *   - Camera shake scaled by proximity (nearby kills feel real)
   */
  /**
   * Handle a `snake_ate` event from the server. If it's our local
   * player, play the same visual reactions World's processFoodEating
   * used to fire locally: chomp, pulse on PERFECT, digesting orb,
   * +N toast, audio cue. Bots' eats are filtered out (rendered locally
   * via the LiveWorldRenderer's body discs — chomp + orbs on bots
   * would be expensive to sync at the eat granularity).
   */
  private handleSnakeAte(e: import('../../net/messages').SnakeAte): void {
    const localId = getLiveClient()?.sessionId;
    if (e.id !== localId) return;
    // Drive the prediction's visual state.
    this.prediction?.applyEat(e.hue, e.mult);
    // Burst + toast + audio at the pellet location.
    if (e.mult >= 2.5) {
      // Perfect match.
      this.world.particles.burst(
        e.x, e.y, e.hue,
        COLOR_MATCH.BURST_COUNT, COLOR_MATCH.BURST_SPEED,
      );
      this.audio.eatPerfect(e.hue);
      if (this.comboTimeout > 0) this.combo += 1;
      else this.combo = 1;
      this.comboTimeout = 1.5;
    } else {
      this.audio.eat(e.hue);
    }
    this.toasts.spawn({
      x: e.x, y: e.y, hue: e.hue, mass: e.gainedMass, multiplier: e.mult,
    });
  }

  private handleSnakeDied(e: import('../../net/messages').SnakeDied): void {
    // Always: particle burst at the location.
    this.world.particles.burst(
      e.hx, e.hy, e.hue,
      COLOR_MATCH.BURST_COUNT * 2,
      COLOR_MATCH.BURST_SPEED * 1.4,
    );
    // Body shatter visual — small ring of secondary particles.
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const d = 20 + Math.random() * 30;
      this.world.particles.burst(
        e.hx + Math.cos(angle) * d,
        e.hy + Math.sin(angle) * d,
        e.hue,
        3, 180,
      );
    }
    // Camera + audio reactions depend on the local player's identity.
    const localId = getLiveClient()?.sessionId;
    if (e.id === localId) {
      // We died.
      this.camera.addShake(SHAKE.DEATH_PLAYER);
      this.audio.death();
      this.audio.boostStop();
    } else if (e.by === localId) {
      // We got the kill.
      this.audio.kill(e.hue);
      const intensity = Math.min(2.0, 0.9 + e.mass / 4000);
      this.celebration.trigger(e.hx, e.hy, e.hue, intensity);
      // Nearby celebration shake.
      const dx = e.hx - this.world.player.head.x;
      const dy = e.hy - this.world.player.head.y;
      const d  = Math.hypot(dx, dy);
      if (d < SHAKE.BOT_NEAR_RANGE) {
        const t = 1 - d / SHAKE.BOT_NEAR_RANGE;
        this.camera.addShake(SHAKE.DEATH_BOT_NEAR * t);
      }
    }
  }

  /**
   * Build + send a snake_state payload describing the local player's
   * current shape. Runs at 10 Hz from setInterval.
   *
   *   - Dead player → skip (don't pollute the relay with corpse state;
   *     remote viewers' TTL will retire the entry, or a respawn
   *     produces a fresh stream).
   *   - Paused tab → skip (we don't want a frozen position broadcast
   *     to other clients while the player can't react).
   *   - No live session → sendSnakeState is a no-op (cheap).
   *
   * Sparse segment sampling: every 3rd segment from the local chain.
   * The renderer rebuilds a smooth spline through whatever it
   * receives, so down-sampling here saves ~3× bandwidth with no
   * visible cost. For a 30-segment mid-game snake, the payload is
   * ~60 floats (~480 bytes JSON, ~150 bytes msgpacked).
   */
  private sendSnakeStateTick = (): void => {
    if (this.paused) return;
    const p = this.world.player;
    if (!p.alive) return;

    // Sparse segment sample — every 3rd to cut bandwidth without
    // breaking the shape. The renderer's spline path through the
    // received points reconstructs a smooth body.
    const segs = p.segments;
    const sample: number[] = [];
    for (let i = 0; i < segs.length; i += 3) {
      const s = segs[i]!;
      sample.push(s.x, s.y);
    }
    // Always include the tail so the receiver doesn't see a
    // truncated body when segment count isn't a multiple of 3.
    if ((segs.length - 1) % 3 !== 0) {
      const tail = segs[segs.length - 1]!;
      sample.push(tail.x, tail.y);
    }

    const payload: ClientSnakeState = {
      hx: p.head.x,
      hy: p.head.y,
      h:  p.heading,
      m:  p.mass,
      s:  sample,
      b:  p.boosting ? 1 : 0,
    };
    sendSnakeState(payload);
  };

  /** Public API exposed to the HUD via the store. */
  respawn(): void {
    if (this.isLiveMode) {
      // Server-authoritative respawn. The server resets state.players
      // [mySessionId].alive = true at spawn position; LocalPrediction
      // .reconcile sees the alive flip and snaps the predicted Snake.
      // We don't touch world.player here (it's a stub in live mode).
      sendRespawn();
    } else {
      this.world.respawnPlayer();
    }
    this.deathTriggeredForPlayer = false;
    this.deathBestUpdated = false;
    this.combo = 0;
    this.comboTimeout = 0;
    this.milestoneFired = 0;
    this.kills = 0;
    this.timeScale = 1;
    const head = this.isLiveMode && this.prediction
      ? this.prediction.snake.head
      : this.world.player.head;
    this.camera.snapTo(head.x, head.y);
    this.audio.spawn(this.world.player.teamHue);
    // Clear the "killed by X" so the next death starts fresh. Recap is
    // already ended by the player tapping Play Again (or the timer), so
    // we just send the cleared flag again as a safety.
    this.onHud({
      killedByName: null,
      killedByHue: null,
      recapActive: false,
      recapKillerName: null,
      recapKillerHue: null,
      recapRank: 0,
      recapFinalMass: 0,
    });
  }

  /** Mute/unmute the audio engine. Returns current muted state. */
  toggleMute(): boolean {
    return this.audio.toggleMute();
  }

  /** Snapshot the leaderboard at this instant and find the player's rank
   *  (1-based). Used by the death recap so the player sees the final rank
   *  they reached before dying — including themselves in the sort even
   *  though they're now `alive=false`. */
  private computePlayerRank(): number {
    const player = this.world.player;
    let rank = 1;
    for (let i = 0; i < this.world.snakes.length; i++) {
      const s = this.world.snakes[i]!;
      if (s.id === player.id) continue;
      // Only count snakes that were alive at death (someone the player
      // was actually competing against). Dead bots aren't "above" you.
      if (!s.alive) continue;
      if (s.mass > player.mass) rank++;
    }
    return rank;
  }

  /** Begin the 1.5s cinematic. Locks the camera onto the killer + sends
   *  recap fields through the HUD push so the overlay can render. Also
   *  ducks the music ~70% for the duration of the cinematic so the
   *  overlay text dominates the moment. */
  private startRecap(victim: Snake, killer: Snake): void {
    this.recap.active = true;
    this.recap.killer = killer;
    this.recap.killerName = killer.name;
    this.recap.killerHue = killer.teamHue;
    this.recap.finalMass = Math.round(victim.mass);
    this.recap.rank = this.computePlayerRank();
    this.recap.remaining = this.recap.duration;
    this.audio.duckMusic(0.7, this.recap.duration + 0.2);
    this.onHud({
      recapActive: true,
      recapKillerName: killer.name,
      recapKillerHue: killer.teamHue,
      recapRank: this.recap.rank,
      recapFinalMass: this.recap.finalMass,
    });
  }

  /** Manually end the recap window — bound to the overlay's Skip button. */
  skipRecap(): void {
    if (!this.recap.active) return;
    this.endRecap();
  }

  private endRecap(): void {
    this.recap.active = false;
    this.recap.remaining = 0;
    this.recap.killer = null;
    this.onHud({ recapActive: false });
  }

  /** Toggle the pause state — bound to Escape and the in-HUD pause modal. */
  togglePause(): void {
    this.paused = !this.paused;
    if (this.paused) this.audio.boostStop();
    this.onHud({ paused: this.paused });
  }

  /** Mobile HUD: hold-to-boost / tap-to-eject. Delegates to InputManager. */
  setBoost(on: boolean): void { this.input.setBoost(on); }
  queueEject(): void { this.input.queueEject(); }
  /** Mobile HUD: virtual joystick angle in radians (world space) or
   *  null when the player isn't touching the joystick. */
  setMobileSteer(angle: number | null): void { this.input.setMobileSteer(angle); }

  /** Switch the canvas palette between dark (default neon) and light. */
  setLightMode(on: boolean): void { this.lightMode = on; }

  /** Toggle the player's gold-border cosmetic (60s reward). Called by
   *  GameCanvas in response to the store flag flipping, and also on
   *  respawn so the new player snake instance picks up the halo. */
  setPlayerGoldBorder(on: boolean): void {
    this.world.player.goldBorder = on;
  }

  /** Apply a player display name — propagates to the leaderboard live. */
  setPlayerName(name: string): void {
    this.world.setPlayerName(name);
  }

  // ---------------------------------------------------------------------------
  // Resize
  // ---------------------------------------------------------------------------

  private attachResize(): void {
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', this.resize);
      return;
    }
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(this.canvas);
  }

  private resize = (): void => {
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    this.dpr = clamp(window.devicePixelRatio || 1, 1, 2);
    this.canvas.width = Math.floor(w * this.dpr);
    this.canvas.height = Math.floor(h * this.dpr);
    this.camera.setViewport(w, h);
  };

  // ---------------------------------------------------------------------------
  // Physics tick (fixed-dt) — invoked 0..N times per rAF by GameLoop
  // ---------------------------------------------------------------------------

  private update = (dt: number): void => {
    // Resolve the local snake head for the mobile-joystick path —
    // InputManager's refresh uses this to project the steer target
    // out from the head when the joystick is active. In live mode
    // we read from the prediction snake; offline from world.player.
    const liveHead = this.isLiveMode ? this.prediction?.snake.head : null;
    const headX = liveHead?.x ?? this.world.player.head.x;
    const headY = liveHead?.y ?? this.world.player.head.y;
    this.input.refresh(this.camera, headX, headY);

    // Tab unfocused → freeze physics + audio. Render keeps drawing the
    // last state so the canvas doesn't go black.
    if (this.paused) return;

    // Hit-stop microfreeze after a player-credited kill — burns down even
    // while frozen so the world unblocks within ~60ms regardless of fps.
    if (this.hitStop > 0) {
      this.hitStop -= dt;
      return;
    }

    const player = this.world.player;

    // Track death moment to persist the best mass.
    if (!player.alive && !this.deathBestUpdated) {
      this.deathBestUpdated = true;
      if (player.mass > this.bestMass) {
        this.bestMass = Math.round(player.mass);
        saveBestMass(this.bestMass);
      }
    }

    if (player.alive) {
      player.steerTargetRaw.x = this.input.mouseWorld.x;
      player.steerTargetRaw.y = this.input.mouseWorld.y;
      player.boosting = this.input.boost;

      // 60-second session-reward beat. Fires when ANY single life
      // crosses the 60s mark — death before then resets the counter
      // (player.spawnTime is set on each respawn in World), so a
      // perpetual respawner doesn't accidentally trigger after a
      // chain of 10-second deaths. Once fired, the engine flag
      // guards against re-fire for the rest of this engine's life;
      // the store flag guards against re-fire across navigation.
      if (!this.sessionRewardFired && this.time - player.spawnTime >= 60) {
        this.sessionRewardFired = true;
        this.onSessionReward?.();
      }

      // Eject (W) with cooldown.
      this.ejectCooldown -= dt;
      if (this.input.consumeEject() && this.ejectCooldown <= 0) {
        if (this.world.ejectFromPlayer()) {
          this.ejectCooldown = 0.12;
          this.audio.eject(player.teamHue);
        }
      }
    } else if (!this.deathTriggeredForPlayer) {
      this.deathTriggeredForPlayer = true;
    }

    // Boost audio — start when boost goes high, stop when low.
    const boostingNow = player.alive && player.boosting && player.mass > 220;
    if (boostingNow && !this.wasBoosting) this.audio.boostStart();
    else if (!boostingNow && this.wasBoosting) {
      this.audio.boostStop();
      this.audio.boostRelease();
    }
    this.wasBoosting = boostingNow;

    // Slow-motion on death: ramp timeScale toward 0.3 when dead, 1.0 alive.
    const targetTimeScale = player.alive ? 1.0 : 0.32;
    this.timeScale = damp(this.timeScale, targetTimeScale, 7, dt);
    const simDt = dt * this.timeScale;

    // Music intensity: maps player mass to 0..1 over a compressed range
    // (200 → 12000) so the player feels the music grow during normal
    // gameplay, not only after reaching giant mass. Damp faster (stiffness
    // 1.6 ≈ 0.6 s half-time) so swells track the gameplay arc.
    const targetIntensity = player.alive
      ? Math.max(0, Math.min(1, (player.mass - 200) / 11800))
      : 0;
    this.musicIntensity = damp(this.musicIntensity, targetIntensity, 1.6, dt);
    this.audio.setMusicIntensity(this.musicIntensity);

    this.time += simDt;

    // Combo timer ticks in real time (not simDt) so death doesn't pause it.
    if (this.comboTimeout > 0) {
      this.comboTimeout -= dt;
      if (this.comboTimeout <= 0) this.combo = 0;
    }

    // World simulation runs in simDt (slowed during death). In live
    // mode the server owns the sim — we skip it locally and let the
    // patched server state drive what's rendered. The local world
    // object is still kept around because some visual subsystems
    // (particles, celebrations, ambient dust) still tick off it
    // and reading from a partly-zeroed world would look wrong.
    if (!this.isLiveMode) {
      this.world.update(simDt);
    } else {
      // Phase-2-Full Session 5 — client-side prediction loop.
      //   1) Step prediction with current input (mouseWorld + boost).
      //      Snake.update runs the SAME physics the server does,
      //      keeping the predicted state on-axis with the
      //      authoritative state idle-input.
      //   2) Reconcile to the latest server-side view of us on every
      //      frame. Cheap: just a few field reads + lerps. If the
      //      patch arrived during this frame, the prediction will
      //      pull toward the new authority; otherwise we re-blend
      //      toward the unchanged target (no-op when already snug).
      if (this.prediction) {
        this.prediction.step(
          simDt,
          this.input.mouseWorld.x,
          this.input.mouseWorld.y,
          this.input.boost,
        );
        const serverMe = getLocalServerPlayer();
        if (serverMe) this.prediction.reconcile(serverMe, this.time);
      }
      // Particles + toasts + celebration still tick locally so
      // snake_died bursts and snake_ate toasts animate normally.
      this.world.particles.step(simDt);
      this.toasts.step(simDt);
      this.celebration.step(simDt);
    }

    // ── Approach warning ──
    // Edge-trigger an "uh-oh" sound when a rival that's ≥1.5× the
    // player's mass enters close range. Cooldown of 1.5s + dedupe set
    // means the ping only fires on FIRST entry, never spams.
    this.approachWarnCooldown -= dt;
    if (player.alive) {
      const currentThreats = new Set<number>();
      const wRange = 550; // tighter than the visual indicator (700) so
                         //  the sound is a "they're close" warning, not
                         //  a "they exist" alarm.
      for (let i = 0; i < this.world.snakes.length; i++) {
        const o = this.world.snakes[i]!;
        if (o.isPlayer || !o.alive) continue;
        const ratio = o.mass / Math.max(1, player.mass);
        if (ratio < 1.5) continue;
        const dx = o.head.x - player.head.x;
        const dy = o.head.y - player.head.y;
        if (dx * dx + dy * dy < wRange * wRange) {
          currentThreats.add(o.id);
          if (!this.prevThreats.has(o.id) && this.approachWarnCooldown <= 0) {
            this.audio.approachWarning();
            this.approachWarnCooldown = 1.5;
          }
        }
      }
      this.prevThreats = currentThreats;
    } else {
      this.prevThreats.clear();
    }
    // Atmosphere + UI effects step in slowed time too, so slow-mo affects everything.
    this.dust.step(simDt);
    this.toasts.step(simDt);
    this.celebration.step(simDt);
    // Speed lines: intensity proportional to boost — fades quickly when not boosting.
    const speedLineIntensity = boostingNow ? 1 : 0;
    this.speedLines.step(simDt, speedLineIntensity, this.camera.viewportW, this.camera.viewportH);

    // Personal-best milestone celebration — fires once per 1000-mass step
    // above the snake's previous best. Skip during the first 0.5s of life
    // so the spawn flash doesn't get drowned.
    if (player.alive && this.world.time > 0.5) {
      const currentMilestone = Math.floor(player.mass / 1000);
      if (currentMilestone > this.milestoneFired && player.mass > this.bestMass) {
        this.milestoneFired = currentMilestone;
        this.celebration.trigger(
          player.head.x, player.head.y, player.teamHue,
          1.3 + Math.min(0.6, currentMilestone * 0.05),
        );
        this.audio.milestone(player.teamHue);
      }
    }

    // ── Camera target ──
    // PRIORITY 1: Death-recap cinematic — when active, the camera locks
    // onto the killer's head and pulls in for a tighter, dramatic frame.
    // The recap timer counts down in real time (not simDt) so the
    // existing slow-mo doesn't stretch the window.
    if (this.recap.active && this.recap.killer) {
      this.recap.remaining -= dt;
      const k = this.recap.killer;
      // If the killer dies mid-recap (got killed themselves), bail
      // gracefully — go straight to the modal.
      if (!k.alive || this.recap.remaining <= 0) {
        this.endRecap();
      } else {
        this.camera.target.x = k.head.x;
        this.camera.target.y = k.head.y;
        // Tighter zoom for a cinematic look. Uses the player's last
        // viewport scale so the dramatic punch-in is relative to where
        // the camera was, not an absolute number.
        const minDim = Math.min(this.camera.viewportW, this.camera.viewportH);
        const viewportScale = Math.max(
          CAMERA.VIEWPORT_SCALE_MIN,
          Math.min(CAMERA.VIEWPORT_SCALE_MAX, minDim / CAMERA.VIEWPORT_REF),
        );
        const killerSizeZoom = Math.pow(PLAYER.START_RADIUS / Math.max(k.radius, 1), CAMERA.ZOOM_POWER);
        // ~1.4× pull-in. Camera's own smoothing makes this a smooth
        // dolly, not a snap.
        this.camera.setZoomTarget(killerSizeZoom * viewportScale * 1.4);
      }
    } else if (player.alive) {
      // PRIORITY 2: Normal head-follow with velocity lookahead. In
      // live mode, the camera tracks the SERVER's view of the local
      // player so it stays aligned with what the player visually
      // sees on screen. Without this the cursor → head offset would
      // diverge by the round-trip latency, making aim feel mushy.
      let headX: number, headY: number, headingForLookahead: number;
      let speedForLookahead: number, radiusForZoom: number, boostForZoom: boolean;
      // Camera tracks the PREDICTED snake in live mode — that's the
      // visual the player perceives moving with their mouse. Without
      // this, the cursor → head offset would drift by the round-trip
      // latency and the snake would feel mushy. Prediction's
      // `snake.head` is updated each frame from the freshest input.
      const predSnake = this.isLiveMode ? this.prediction?.snake : null;
      if (predSnake && predSnake.alive) {
        headX = predSnake.head.x;
        headY = predSnake.head.y;
        headingForLookahead = predSnake.heading;
        speedForLookahead = predSnake.currentSpeed;
        radiusForZoom = predSnake.radius;
        boostForZoom = predSnake.boosting;
      } else {
        headX = player.head.x;
        headY = player.head.y;
        headingForLookahead = player.heading;
        speedForLookahead = player.currentSpeed;
        radiusForZoom = player.radius;
        boostForZoom = player.boosting;
      }
      const vx = Math.cos(headingForLookahead) * speedForLookahead;
      const vy = Math.sin(headingForLookahead) * speedForLookahead;
      this.camera.target.x = headX + vx * CAMERA.LOOKAHEAD_TIME;
      this.camera.target.y = headY + vy * CAMERA.LOOKAHEAD_TIME;
      const sizeZoom = Math.pow(PLAYER.START_RADIUS / Math.max(radiusForZoom, 1), CAMERA.ZOOM_POWER);
      const boostZoom = boostForZoom ? 0.93 : 1.0;
      // Viewport adaptation: keep the snake at a consistent on-screen size
      // regardless of display resolution. min(vw,vh) / 720 gives a smooth
      // scale factor — bigger displays zoom in proportionally so the
      // snake doesn't shrink and the cursor doesn't "get lost".
      const minDim = Math.min(this.camera.viewportW, this.camera.viewportH);
      const viewportScale = Math.max(
        CAMERA.VIEWPORT_SCALE_MIN,
        Math.min(CAMERA.VIEWPORT_SCALE_MAX, minDim / CAMERA.VIEWPORT_REF),
      );
      this.camera.setZoomTarget(sizeZoom * boostZoom * viewportScale);
    }
    // PRIORITY 3: when dead and recap is over, the camera drifts on its
    // last-known target — the DeathModal owns the screen.
    this.camera.update(dt);

    // HUD push.
    //
    // In live mode the local World.player is a stub that never advances
    // — so reading its `alive` / `mass` would always report "alive,
    // mass=1098" and DeathModal would never fire. Pull from prediction
    // when live, falls through to world.player for training mode.
    const livePlayer = this.isLiveMode ? this.prediction?.snake : null;
    const hudAlive  = livePlayer ? livePlayer.alive : player.alive;
    const hudMass   = livePlayer ? livePlayer.mass  : player.mass;
    const hudRadius = livePlayer ? livePlayer.radius : player.radius;
    const hudPosX   = livePlayer ? livePlayer.head.x : player.head.x;
    const hudPosY   = livePlayer ? livePlayer.head.y : player.head.y;
    this.hudAccum += dt;
    if (this.hudAccum >= this.hudInterval) {
      this.hudAccum = 0;
      this.onHud({
        fps: this.loop.fps,
        mass: hudMass,
        radius: hudRadius,
        posX: hudPosX,
        posY: hudPosY,
        zoom: this.camera.zoom,
        alive: hudAlive,
        playerHue: player.teamHue,
        combo: this.combo,
        bestMass: this.bestMass,
        muted: this.audio.isMuted,
        paused: this.paused,
        audioLocked: this.audio.isLocked(),
        surgeRemaining: player.surgeRemaining,
        doubleRemaining: player.doubleMassRemaining,
        shielded: player.shielded,
        kills: this.kills,
        comboRemaining: this.comboTimeout,
        aliveSeconds: player.alive ? Math.max(0, this.time - player.spawnTime) : 0,
      });
    }

    // Leaderboard push.
    this.leaderAccum += dt;
    if (this.leaderAccum >= this.leaderInterval) {
      this.leaderAccum = 0;
      this.onLeaderboard(this.computeLeaderboard());
    }
  };

  private computeLeaderboard(): LeaderRow[] {
    const TOP = 8;

    // In live (server-authoritative) mode the world.snakes array is a
    // stub left over from offline mode and never updates — reading
    // from it produces a leaderboard frozen on the room's initial
    // bots, which is the "leaderboard never moves" bug the player
    // reported. The authoritative pool is serverPlayers, mirrored
    // every patch from the Colyseus state.
    if (this.isLiveMode) {
      const localId = this.prediction?.sessionId ?? null;
      const live = [...serverPlayers.values()].filter((p) => p.alive);
      live.sort((a, b) => b.mass - a.mass);
      const top: LeaderRow[] = live.slice(0, TOP).map((p) => ({
        id: p.id,
        name: p.name,
        mass: p.mass,
        isPlayer: p.id === localId,
        hue: p.hue,
      }));
      if (localId && !top.some((r) => r.isPlayer)) {
        const idx = live.findIndex((p) => p.id === localId);
        const me = idx >= 0 ? live[idx]! : null;
        if (me) {
          top.push({
            id: me.id,
            name: me.name,
            mass: me.mass,
            isPlayer: true,
            hue: me.hue,
            playerRank: idx + 1,
          });
        }
      }
      return top;
    }

    const snakes = this.world.snakes.filter((s) => s.alive);
    snakes.sort((a, b) => b.mass - a.mass);
    const top: LeaderRow[] = snakes.slice(0, TOP).map((s) => ({
      id: s.id,
      name: s.name,
      mass: s.mass,
      isPlayer: s.isPlayer,
      hue: s.teamHue,
    }));
    const player = this.world.player;
    if (player.alive && !top.some((r) => r.isPlayer)) {
      const idx = snakes.findIndex((s) => s.isPlayer);
      top.push({
        id: player.id,
        name: player.name,
        mass: player.mass,
        isPlayer: true,
        hue: player.teamHue,
        playerRank: idx >= 0 ? idx + 1 : null,
      });
    }
    return top;
  }

  // ---------------------------------------------------------------------------
  // Render — invoked once per rAF by GameLoop
  // ---------------------------------------------------------------------------

  private render = (): void => {
    const ctx = this.ctx;
    const vw = this.camera.viewportW;
    const vh = this.camera.viewportH;

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    this.grid.drawBackground(ctx, vw, vh, this.world.player.teamHue, this.lightMode);
    this.grid.drawGrid(ctx, this.camera, this.lightMode);
    // Ambient dust sits between the grid and the world border so it reads as
    // "in the air" rather than "above the playfield".
    this.dustRenderer.draw(ctx, this.camera, this.dust, this.time);
    const ph = this.world.player.head;
    this.grid.drawWorldBorder(ctx, this.camera, ph.x, ph.y, this.time, this.world.player.teamHue);

    if (this.isLiveMode) {
      // Phase-2-Full render order: food + remote snakes from server
      // (LiveWorldRenderer), then the predicted local snake on top via
      // the legacy SnakeRenderer — that path gives us tongue flicks,
      // chomp animation, digesting orbs, leader aura, all the visual
      // richness the server doesn't sync. The predicted snake's id is
      // excluded from LiveWorldRenderer so we don't render twice.
      const localId = this.prediction?.sessionId ?? null;
      this.liveWorldRenderer.drawAll(
        ctx, this.camera, this.time, this.world.player.teamHue, localId,
      );
      if (this.prediction?.snake.alive) {
        this.snakeRenderer.draw(
          ctx, this.camera, this.prediction.snake, this.time, this.prediction.snake,
        );
      }
      // Powerups are still client-only for now — Session 5 may
      // move them to authoritative spawning, but for live alpha
      // we just skip the layer.
    } else {
      this.foodRenderer.draw(ctx, this.camera, this.world.food, this.time, this.world.player.teamHue);
      this.powerupRenderer.draw(ctx, this.camera, this.world.powerups.powerups, this.time);

      const sorted = sortSnakesForRender(this.world.snakes, this.world.player.head.x, this.world.player.head.y);
      for (let i = 0; i < sorted.length; i++) {
        this.snakeRenderer.draw(ctx, this.camera, sorted[i]!, this.time, this.world.player);
      }
    }

    // Phase-2-Lite remote pass. Painted ABOVE the local snakes so a
    // remote player who happens to share screen-space with a local
    // bot doesn't visually disappear under the bot. Render-delay of
    // 100 ms inside the renderer handles smoothing — see
    // RemoteSnakeRenderer for the math.
    this.remoteSnakeRenderer.drawAll(
      ctx,
      this.camera,
      this.time,
      getLiveClient()?.sessionId ?? null,
    );

    this.particleRenderer.draw(ctx, this.camera, this.world.particles);
    // Celebration sits above ambient particles but below toasts/HUD —
    // it's part of the gameplay scene, not the UI.
    this.celebrationRenderer.draw(ctx, this.camera, this.celebration);
    this.toastRenderer.draw(ctx, this.camera, this.toasts);

    // Speed lines — drawn above gameplay so they read as foreground motion.
    this.speedLines.draw(ctx, this.world.player.teamHue);

    // Cinematic vignette — darkens the screen edges, gives film polish.
    this.grid.drawVignette(ctx, vw, vh);

    // Minimap is a UI overlay — always on top of the vignette.
    // Skipped on mobile per `renderMinimap` flag so the small screen
    // isn't cluttered with a radar the player doesn't need.
    if (this.renderMinimap) {
      this.minimap.draw(
        ctx,
        this.camera,
        this.world.snakes,
        this.world.player,
        this.time,
        this.world.powerups.powerups,
        this.lightMode,
      );
    }

    // Death-flash overlay — full-viewport color tint that decays on the
    // Camera. Painted ABOVE everything in-game (incl. minimap & vignette)
    // so the punch is unmistakable, but UNDER the crosshair so input
    // tracking stays unobstructed.
    if (this.camera.flashAlpha > 0.005) {
      const a = this.camera.flashAlpha;
      const hue = this.camera.flashColor;
      ctx.save();
      ctx.fillStyle = `hsla(${hue} 100% 70% / ${a})`;
      ctx.fillRect(0, 0, vw, vh);
      ctx.restore();
    }

    // Custom crosshair cursor, drawn in the player's team color.
    this.drawCrosshair(ctx);
  }

  /**
   * Cursor reticle — scales with viewport so it never gets "lost" on a
   * big display, and breathes a soft alpha pulse for easy tracking.
   */
  private drawCrosshair(ctx: CanvasRenderingContext2D): void {
    const x = this.input.mouseScreen.x;
    const y = this.input.mouseScreen.y;
    if (x <= 0 && y <= 0) return;
    const hue = this.world.player.teamHue;

    // Reticle size scales with the smaller viewport dimension. On a 720px
    // display the reticle is ~8px; on a 1440px display ~16px; on 4K ~24px.
    const minDim = Math.min(this.camera.viewportW, this.camera.viewportH);
    const scale = Math.max(1, Math.min(3, minDim / 720));
    const r = 8 * scale;
    const gap = 3 * scale;
    const lineWidth = 1.5 * scale;
    const dotR = Math.max(1.4, 1.3 * scale);

    // Subtle alpha pulse — keeps the reticle scannable on busy scenes.
    const pulse = 0.78 + 0.22 * Math.sin(this.time * 2.6);

    ctx.save();
    ctx.globalAlpha = pulse;
    ctx.strokeStyle = `hsl(${hue} 100% 72%)`;
    ctx.shadowColor = `hsl(${hue} 100% 60%)`;
    ctx.shadowBlur = 6 * scale;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    ctx.moveTo(x - r, y); ctx.lineTo(x - gap, y);
    ctx.moveTo(x + gap, y); ctx.lineTo(x + r, y);
    ctx.moveTo(x, y - r); ctx.lineTo(x, y - gap);
    ctx.moveTo(x, y + gap); ctx.lineTo(x, y + r);
    ctx.stroke();
    ctx.fillStyle = `hsl(${hue} 100% 82%)`;
    ctx.beginPath();
    ctx.arc(x, y, dotR, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

const _renderSort: Snake[] = [];
function sortSnakesForRender(snakes: Snake[], px: number, py: number): Snake[] {
  _renderSort.length = 0;
  for (let i = 0; i < snakes.length; i++) {
    const s = snakes[i]!;
    if (s.alive) _renderSort.push(s);
  }
  _renderSort.sort((a, b) => {
    if (a.isPlayer) return 1;
    if (b.isPlayer) return -1;
    const dax = a.head.x - px;
    const day = a.head.y - py;
    const dbx = b.head.x - px;
    const dby = b.head.y - py;
    return dbx * dbx + dby * dby - (dax * dax + day * day);
  });
  return _renderSort;
}
